// Only records that have a FileVine ID have been synced.

import { CloudUploadIcon, CheckCircleIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { Badge } from "../components/atoms/Badge";
import { Button } from "../components/atoms/Button";
import { Theme } from "../components/atoms/theme";
import { Tooltip } from "../components/atoms/Tooltip";
import { ModalWithTwoButtons } from "../components/molecules/Modal";
import useToast from "../hooks/useToast";
import {
  useUploadFileVineRecordMutation,
  useVerifyFileVineRecordQuery,
} from "../redux/reduxQuery/fileVineApi";

// (ONLY WORKS WITH PROVIDERS AND PATIENTS)
export const isRecordSyncedWithFileVine = (record) =>
  record && !!record.fileVineID;

export const UploadFVButton: React.FC<{
  record: any;
  type: "patient" | "provider";
  modalTitle: string;
  modalContent: string;
  refetch: any;
}> = (props) => {
  const [openModalFVSync, setOpenModalFVSync] = useState(false);
  const [uploadToFileVine] = useUploadFileVineRecordMutation();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const alreadySynced = isRecordSyncedWithFileVine(props.record);
  const handleButtonClick = () => {
    if (alreadySynced)
      window.open(
        `https://rayna.filevineapp.com/#/addressbook/8443/contact/${props.record.fileVineID}`,
        "_blank"
      );
    else {
      setOpenModalFVSync(true);
    }
  };
  return (
    <>
      <Button
        icon={true}
        color={
          alreadySynced ? Theme.colors.success_000 : Theme.colors.warning_000
        }
        hoverColor={
          alreadySynced
            ? Theme.colors.success_neg100
            : Theme.colors.warning_neg100
        }
        noText={true}
        buttonText={alreadySynced ? "Uploaded" : "Upload"}
        img={
          alreadySynced ? (
            <CheckCircleIcon
              width={"1.25rem"}
              color={Theme.colors.neutrals_500}
            />
          ) : (
            <CloudUploadIcon
              width={"1.25rem"}
              color={Theme.colors.neutrals_500}
            />
          )
        }
        onClick={() => handleButtonClick()}
      />
      {openModalFVSync && (
        <ModalWithTwoButtons
          title="Upload Record to Filevine"
          content={`Are you sure you want to upload ${props.record.name}'s information to Filevine?`}
          button1Text="cancel"
          button2Text="upload"
          loader={loading}
          onClick1={() => setOpenModalFVSync(false)}
          onClick2={() => {
            setLoading(true);
            uploadToFileVine({
              id: props.record.id,
              type: props.type,
            })
              .unwrap()
              .then(() => {
                toast.success(
                  `Successfully Uploaded ${props.record.name} to Filevine`
                );
                props.refetch();
              })
              .catch((err) => {
                const errorMessage: string =
                  err?.message ||
                  err?.data?.message ||
                  String(JSON.stringify(err));
                if (errorMessage && errorMessage.includes("Please enter"))
                  toast.error(errorMessage);
                else
                  toast.error(
                    `Unable to Upload ${props.record.name} to Filevine: ${errorMessage}`
                  );
              })
              .finally(() => {
                setOpenModalFVSync(false);
                setLoading(false);
              });
          }}
        />
      )}
    </>
  );
};

export const RenderCell = (cellProps: any, scrollPosition?: number) => {
  const isSyncedWithFileVine = isRecordSyncedWithFileVine(
    cellProps.row.original
  );
  if (isSyncedWithFileVine)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "start",
          gap: "0.5rem",
        }}
      >
        <Tooltip
          text="This record has been created in Filevine"
          position="top"
          background="black"
          scrollPosition={scrollPosition}
        >
          <Badge color={Theme.colors.success_000} icon />
        </Tooltip>

        <VerifyFilevineCell row={cellProps.row} />
      </div>
    );
  else
    return (
      <Tooltip
        text="This record has not been created in FileVine"
        position="top"
        background="black"
        scrollPosition={scrollPosition}
      >
        <Badge
          color={Theme.colors.warning_000}
          icon
          img={
            <CloudUploadIcon
              width={"0.938rem"}
              color={Theme.colors.neutrals_500}
            />
          }
        />
      </Tooltip>
    );
};

const VerifyFilevineCell = ({ row }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const toast = useToast();
  const fileVineId = row.original.fileVineID;

  // Only run the query when the button is clicked
  // disable for now
  const shouldVerify = false && fileVineId && row.original?.email;

  const {
    data,
    isFetching: isLoading,
    error: queryError,
    refetch,
  } = useVerifyFileVineRecordQuery(row.original?.email, {
    skip: !shouldVerify,
  });

  console.log({ isLoading, queryError });

  // Handle verification result
  useEffect(() => {
    if (data) {
      if (data.exists) {
        setIsVerified(true);
        toast.success("Record found in Filevine");
      } else {
        toast.error("Record not found in Filevine");
      }
    }
  }, [data, shouldVerify, toast]);

  // Handle query error
  useEffect(() => {
    if (queryError && shouldVerify) {
      toast.error("Error verifying record");
    }
  }, [queryError, shouldVerify, toast]);

  // const verifyFilevineRecord = () => {
  //   if (!fileVineId) {
  //     toast.error("No Filevine ID found. Please upload to Filevine first.");
  //     return;
  //   }

  //   setIsVerifying(true);
  //   setShouldVerify(true);
  // };

  // Reset verifying state when query completes
  useEffect(() => {
    if (isVerifying && !isLoading && shouldVerify === false) {
      setIsVerifying(false);
    }
  }, [isVerifying, isLoading, shouldVerify]);

  if (!shouldVerify) {
    return null;
  }

  const handleVerify = () => {
    refetch();
  };

  return (
    <Button
      icon={false}
      contentColor={
        isLoading
          ? Theme.colors.neutrals_300
          : isVerified
          ? Theme.colors.success_000
          : Theme.colors.warning_000
      }
      hoverColor={Theme.colors.neutrals_500}
      noText={true}
      style={{
        padding: "0",
        margin: "0",
        color: isVerified ? Theme.colors.success_000 : Theme.colors.warning_000,
      }}
      buttonText={isVerified ? "Found in FV" : "Search FV"}
      onClick={handleVerify}
      // disabled={isVerifying || isLoading || isVerified}
      // disabled={isVerifying || isLoading || isVerified}
      img={
        isVerified ? (
          <CheckCircleIcon width={"1rem"} color={Theme.colors.neutrals_500} />
        ) : (
          <CloudUploadIcon width={"1rem"} color={Theme.colors.neutrals_500} />
        )
      }
    />
  );
};
